import type {ImageLoaderProps} from 'next/image'

/**
 * A `next/image` loader that serves Sanity assets straight from the Sanity CDN
 * at the width `next/image` actually asks for.
 *
 * Why this exists rather than either of the two obvious alternatives:
 *
 * - `unoptimized` (what the product gallery did until JO2-206) emits a single
 *   `src` and **no `srcset` at all**, so every device downloads whatever width
 *   happened to be baked into the URL. The gallery baked `w=1600`, so a phone
 *   downloaded six 1600px images to show them at ~390 CSS px. Lighthouse put
 *   the waste at 258 KiB on one product page, and there are 459 products.
 *
 * - Dropping `unoptimized` would route every Sanity image through Vercel's
 *   image optimizer, which re-encodes an asset the Sanity CDN has already
 *   encoded and bills for it per source image.
 *
 * The loader gets both halves: `next/image` generates a real `srcset`, and each
 * entry is a plain Sanity CDN URL, so nothing passes through `/_next/image`.
 *
 * Non-Sanity sources are returned untouched — the loader is set per-element, so
 * this only matters when one element mixes hosts.
 */
export const sanityImageLoader = ({src, width, quality}: ImageLoaderProps): string => {
  let url: URL

  try {
    url = new URL(src)
  } catch {
    // Relative path (a local /public asset). Nothing to rewrite, and building a
    // Sanity URL from it would be wrong.
    return src
  }

  if (url.hostname !== 'cdn.sanity.io') return src

  url.searchParams.set('w', String(width))
  url.searchParams.set('q', String(quality ?? 75))
  // `auto=format` serves WebP/AVIF to browsers that accept them; `fit=max`
  // never upscales past the source, so a small original stays small rather
  // than being stretched to the requested width.
  url.searchParams.set('auto', 'format')
  url.searchParams.set('fit', 'max')

  return url.toString()
}
